import React, { useState } from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import GenericComposedChart from '../../../../charts/GenericComposedChart';
import { Line } from 'recharts';
import { mainColors, greys } from '../../../../../styling/theme';
import { percentageToTwoDecimalPlacesNoHundred } from '../../../../../utilities/numberFormatters';
import { FundEsgTimeSeriesApiResponse } from '../../../../../api/rs-hg/esg';
import { cn } from '../../../../../utilities/cn';

interface EsAlignmentHistoricalChartProps {
  data: FundEsgTimeSeriesApiResponse;
}

interface ChartData {
  date: string;
  aligned_es: number;
  unaligned_es: number;
}

const buildChartData = (data: FundEsgTimeSeriesApiResponse): ChartData[] => {
  if (!data) {
    return [];
  }

  const alignedEs = data.aligned_es || [];
  const unalignedEs = data.unaligned_es || [];

  // Create a map to merge data by date
  const dateMap = new Map<string, ChartData>();

  // Process aligned_es array
  if (Array.isArray(alignedEs)) {
    alignedEs.forEach((item: { date: string; value: number }) => {
      if (item && item.date) {
        dateMap.set(item.date, {
          date: item.date,
          // Multiply by 100 to convert from decimal to percentage
          aligned_es: (item.value || 0) * 100,
          unaligned_es: 0, // Will be filled from unaligned_es array
        });
      }
    });
  }

  // Process unaligned_es array and merge with existing dates
  if (Array.isArray(unalignedEs)) {
    unalignedEs.forEach((item: { date: string; value: number }) => {
      if (item && item.date) {
        const existing = dateMap.get(item.date);
        if (existing) {
          // Multiply by 100 to convert from decimal to percentage
          existing.unaligned_es = (item.value || 0) * 100;
        } else {
          dateMap.set(item.date, {
            date: item.date,
            aligned_es: 0,
            // Multiply by 100 to convert from decimal to percentage
            unaligned_es: (item.value || 0) * 100,
          });
        }
      }
    });
  }

  // Convert map to array and sort by date (newest first)
  return Array.from(dateMap.values()).sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
};

const tooltipFormatter = (value: any, name: any) => {
  return [
    percentageToTwoDecimalPlacesNoHundred(value),
    name === 'aligned_es' ? 'Aligned E/S' : 'Unaligned E/S',
  ];
};

const legendFormatter = (value: any) => {
  return value === 'aligned_es' ? 'Aligned E/S' : 'Unaligned E/S';
};

const EsAlignmentHistoricalChart: React.FC<
  EsAlignmentHistoricalChartProps
> = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState<
    'aligned_es' | 'unaligned_es'
  >('aligned_es');

  const chartData = buildChartData(data);


  const headerActions = <div className="flex gap-4 ">
  <button
    className={cn(
      'border-none outline-none transition-all duration-200 rounded px-5 py-0.5 text-xs h-8 text-white font-medium cursor-pointer drop-shadow-sm',
      selectedMetric === 'aligned_es'
        ? ''
        : 'bg-gray-400 hover:opacity-75',
    )}
    style={
      selectedMetric === 'aligned_es'
        ? { backgroundColor: mainColors.mainBlue }
        : undefined
    }
    onClick={() => setSelectedMetric('aligned_es')}
  >
    Aligned E/S
  </button>
  <button
    className={cn(
      'border-none outline-none transition-all duration-200 rounded px-5 py-0.5 text-xs h-8 text-white font-medium cursor-pointer drop-shadow-sm',
      selectedMetric === 'unaligned_es'
        ? ''
        : 'bg-gray-400 hover:opacity-75',
    )}
    style={
      selectedMetric === 'unaligned_es'
        ? { backgroundColor: mainColors.Pass }
        : undefined
    }
    onClick={() => setSelectedMetric('unaligned_es')}
  >
    Unaligned E/S
  </button>
</div>

  return (
    <GridItem xs={12} card>
      <div className="p-4">
        <GenericComposedChart<ChartData>
        loading={!chartData || chartData.length === 0}
        data={chartData}
          title={`Historical ${
            selectedMetric === 'aligned_es' ? 'Aligned E/S' : 'Unaligned E/S'
          }`}
          headerActions={headerActions}
        export
        download
        height={400}
        width={'100%'}
        margin={{ top: 50, right: 50, left: 60, bottom: 50 }}
        xAxes={[
          {
            dataKey: 'date',
            interval: 'preserveStartEnd',
            type: 'category',
            minTickGap: 70,
          },
        ]}
        yAxes={[
          {
            label: {
              value: 'Value %',
              angle: -90,
              position: 'left',
              offset: 30,
              style: {
                fill: greys.grey700,
                fontSize: '130%',
                fontWeight: 400,
                color: mainColors.mainBlue,
              },
            },
            tickFormatter: (val) => percentageToTwoDecimalPlacesNoHundred(val),
          },
        ]}
        legendFormatter={legendFormatter}
        tooltipFormatter={tooltipFormatter}
        components={[
          {
            ComponentType: Line,
            props: {
              type: 'monotone',
              dataKey: selectedMetric,
              stroke:
                selectedMetric === 'aligned_es'
                  ? mainColors.mainBlue
                  : mainColors.Pass,
              strokeWidth: 2,
              dot: { r: 3 },
              activeDot: { r: 5 },
            },
          },
        ]}
      />
      </div>
    </GridItem>
  );
};

export default EsAlignmentHistoricalChart;

